import React, { useState } from 'react';
import { UsersIcon, MapPinIcon, MessageSquareIcon, EyeIcon, PaletteIcon, BarChart3Icon, SettingsIcon, RocketIcon, ChevronDownIcon, ClipboardListIcon, PrinterIcon, DownloadIcon } from 'lucide-react';
interface Section {
  id: string;
  icon: React.ElementType;
  title: string;
  subtitle: string;
  color: string;
  questions: {
    question: string;
    context?: string;
    type: 'open' | 'choice' | 'priority';
  }[];
}
const sections: Section[] = [{
  id: 'objective',
  icon: ClipboardListIcon,
  title: 'Objective & Success Criteria',
  subtitle: 'Start here — what are we trying to achieve?',
  color: '#0F172A',
  questions: [{
    question: 'What is the primary objective of this interactive quiz?',
    context: 'e.g. lead generation, brand awareness, audience education, market research, event engagement, thought leadership positioning',
    type: 'open'
  }, {
    question: 'Are there secondary objectives?',
    context: 'e.g. the primary goal may be lead capture, but a secondary goal could be collecting market insight data or positioning Minerva as a thought leader.',
    type: 'open'
  }, {
    question: 'What does success look like for this project?',
    context: 'Consider multiple dimensions: credibility/ethos positioning, market insight generation, lead capture, audience engagement, thought leadership, conversation starters at events, etc.',
    type: 'open'
  }, {
    question: 'How should we prioritize these success dimensions? Rank in order of importance.',
    context: 'e.g. 1) Building credibility and trust with the oncology community, 2) Gathering real market insights on priorities and gaps, 3) Generating qualified leads, 4) Creating reusable content from aggregate data',
    type: 'priority'
  }, {
    question: 'Who are the internal stakeholders and what does each need from this?',
    context: 'e.g. Marketing needs leads, Commercial needs conversation starters, Medical Affairs needs compliant content, Leadership needs market insight',
    type: 'open'
  }, {
    question: 'Is there an existing KPI framework this should plug into?',
    context: 'e.g. marketing qualified leads (MQLs), engagement metrics, content performance dashboards',
    type: 'open'
  }]
}, {
  id: 'audience',
  icon: UsersIcon,
  title: 'Audience & Roles',
  subtitle: 'Who is actually taking this quiz?',
  color: '#2563EB',
  questions: [{
    question: 'Who is the primary target audience for this quiz?',
    context: 'e.g. oncology researchers, pharma executives, imaging specialists, CRO decision-makers',
    type: 'open'
  }, {
    question: 'What are the real role segments we should use for segmentation?',
    context: 'The mock-up uses Strategic / Program / Technical / Partner. Are these accurate? Should we add, remove, or rename any?',
    type: 'open'
  }, {
    question: 'What is the seniority level of the target audience?',
    context: 'C-suite, VP, Director, Manager, Researcher — this affects tone and question complexity.',
    type: 'open'
  }, {
    question: 'How large is the expected audience? What response volume do we anticipate?',
    type: 'open'
  }, {
    question: 'Are there audience segments we should explicitly exclude?',
    context: 'e.g. students, non-oncology researchers, existing customers vs. prospects',
    type: 'open'
  }]
}, {
  id: 'distribution',
  icon: MapPinIcon,
  title: 'When, Where & How',
  subtitle: 'Concrete deployment details',
  color: '#059669',
  questions: [{
    question: 'What is the launch date? Are there hard deadlines?',
    context: 'Provide specific dates. e.g. "Must be live by March 15 for AACR" or "Soft launch in Q2, no fixed date"',
    type: 'open'
  }, {
    question: 'List every specific event, conference, or campaign where this will be used.',
    context: 'Include dates, locations, and booth/session details. e.g. "AACR 2026 (April 5-9, San Diego, Booth #412)", "ASCO 2026 (June, Chicago)", "Email campaign to 2,000 contacts in May"',
    type: 'open'
  }, {
    question: 'What are the exact distribution channels? Check all that apply and add details.',
    context: '☐ Conference booth (which device? iPad, tablet, kiosk?) ☐ Email campaign (which list? how many contacts?) ☐ LinkedIn (organic post or paid ad?) ☐ Website embed (which page?) ☐ QR code on print (which materials?) ☐ Sales team outreach ☐ Other',
    type: 'open'
  }, {
    question: 'What device will users primarily use?',
    context: 'This determines design constraints. e.g. "iPad at booth in portrait mode", "Personal mobile phone via email link", "Desktop via website embed". If multiple, rank by priority.',
    type: 'open'
  }, {
    question: 'What is the realistic time a user will spend?',
    context: 'Be honest about the context. At a busy booth: 60-90 seconds. Via email at desk: 3-5 minutes. This directly impacts how many questions we can include.',
    type: 'open'
  }, {
    question: 'Does the quiz need to work offline or in low-connectivity environments?',
    context: 'Conference WiFi is often unreliable. If yes, we need a PWA/offline-first approach. This affects architecture significantly.',
    type: 'choice'
  }, {
    question: 'Will this be a one-time campaign or an always-on asset?',
    context: 'One-time = simpler, can use static data. Always-on = needs maintenance, content updates, real data collection backend.',
    type: 'choice'
  }, {
    question: 'Who manages this after launch? Is there an internal owner?',
    context: 'Name the person/team responsible for monitoring results, updating content, and handling technical issues.',
    type: 'open'
  }]
}, {
  id: 'content',
  icon: MessageSquareIcon,
  title: 'Content & Questions',
  subtitle: 'What should we actually ask — and why?',
  color: '#D97706',
  questions: [{
    question: 'What are the 3-5 key messages Minerva wants to communicate through this quiz?',
    context: 'The quiz should feel educational, not promotional. What insights should users walk away with?',
    type: 'open'
  }, {
    question: 'Are the current mock-up questions directionally correct, or do they need to be rewritten?',
    context: 'Please review each question in the storyboard and flag any that feel off-target.',
    type: 'open'
  }, {
    question: 'What is the ideal number of questions?',
    context: 'The mock-up has 5 (including role segmentation). More = richer data but higher drop-off. Conference context suggests 3-5.',
    type: 'choice'
  }, {
    question: 'Should questions have a "correct" answer, or should all answers be equally valid?',
    context: 'The mock-up mixes both approaches. A pure opinion-based quiz feels less judgmental but provides less educational value.',
    type: 'choice'
  }, {
    question: 'What tone should the questions and insights use?',
    context: 'Academic/clinical, conversational/accessible, thought-leadership, provocative, etc.',
    type: 'open'
  }, {
    question: 'Are there specific clinical references, data points, or publications that should be cited in the reward insights?',
    type: 'open'
  }]
}, {
  id: 'stealth',
  icon: EyeIcon,
  title: 'Stealth Insights & Data Goals',
  subtitle: 'What does Minerva want to learn from the audience?',
  color: '#DC2626',
  questions: [{
    question: 'Beyond engagement, what data does Minerva want to collect from quiz responses?',
    context: 'e.g. role distribution of audience, awareness of molecular imaging, priority gaps between roles, readiness for platform adoption',
    type: 'open'
  }, {
    question: 'Are there specific hypotheses Minerva wants to validate?',
    context: 'e.g. "Most strategic leaders underestimate biological uncertainty" or "Partner selectors prioritize cost over capability"',
    type: 'open'
  }, {
    question: 'Should individual responses be trackable (e.g. linked to email or lead capture)?',
    context: 'This affects whether we add a lead capture step and privacy/consent requirements.',
    type: 'choice'
  }, {
    question: 'Will aggregate results be used in follow-up content?',
    context: 'e.g. "72% of oncology leaders said X" in a whitepaper, presentation, or social post',
    type: 'open'
  }, {
    question: 'Are there competitive insights Minerva hopes to surface?',
    context: 'e.g. understanding how the audience currently perceives imaging vs. other modalities',
    type: 'open'
  }, {
    question: 'Should we capture any demographic data beyond role?',
    context: 'e.g. company size, geography, years of experience, therapeutic focus area',
    type: 'open'
  }]
}, {
  id: 'design',
  icon: PaletteIcon,
  title: 'Design & Brand',
  subtitle: 'Visual identity, tone, and experience quality',
  color: '#7C3AED',
  questions: [{
    question: 'Is the current dark theme and visual style approved, or should we explore alternatives?',
    context: 'The mock-up uses a dark (#0B1623) background with blue accents. Does this align with Minerva brand guidelines?',
    type: 'choice'
  }, {
    question: 'Are there brand guidelines, color palettes, or font specifications we should follow?',
    context: 'Please share any brand assets, style guides, or approved visual references.',
    type: 'open'
  }, {
    question: 'Should the quiz feel like a Minerva-branded experience or a neutral industry tool?',
    context: 'Branded = trust from existing audience. Neutral = wider reach, less perceived bias.',
    type: 'choice'
  }, {
    question: 'Are the current visualizations (funnel, timeline, comparison panels) the right approach?',
    context: 'Should we use real data visualizations, illustrations, photography, video, or a mix?',
    type: 'open'
  }, {
    question: 'Are there specific images, videos, or assets Minerva wants included?',
    context: 'e.g. product imagery, lab photography, clinical visuals, team photos',
    type: 'open'
  }, {
    question: 'Should the experience include sound or haptic feedback?',
    context: 'Subtle audio cues can enhance engagement at a booth but may be inappropriate in other contexts.',
    type: 'choice'
  }]
}, {
  id: 'results',
  icon: BarChart3Icon,
  title: 'Results & Benchmarking',
  subtitle: 'How should results be presented?',
  color: '#2563EB',
  questions: [{
    question: 'Should poll percentages reflect real collected data or pre-set benchmarks?',
    context: 'Real data requires a backend and grows more valuable over time. Pre-set data is simpler but static.',
    type: 'choice'
  }, {
    question: 'If using real data, do we have existing benchmark data to seed the initial results?',
    context: 'Empty results on day one feel unconvincing. We need seed data from internal surveys, published research, or estimates.',
    type: 'open'
  }, {
    question: 'How important is the role-comparison feature on the results page?',
    context: 'The mock-up shows per-question breakdowns by role. Is this a key differentiator or nice-to-have?',
    type: 'choice'
  }, {
    question: 'Should users be able to share their results?',
    context: 'e.g. "Share your priority profile on LinkedIn" — drives organic reach but requires shareable assets.',
    type: 'choice'
  }, {
    question: 'Should results be emailed to the user?',
    context: 'This creates a natural lead capture moment and extends the experience beyond the quiz.',
    type: 'choice'
  }]
}, {
  id: 'technical',
  icon: SettingsIcon,
  title: 'Technical & Infrastructure',
  subtitle: 'Concrete technical requirements and constraints',
  color: '#6B7280',
  questions: [{
    question: 'What is the exact hosting requirement?',
    context: 'Provide specifics: ☐ Standalone URL (e.g. quiz.minervaimaging.com) ☐ Embedded iframe on minervaimaging.com (which page?) ☐ Deployable to multiple domains ☐ Hosted on client infrastructure. Who manages DNS/hosting?',
    type: 'open'
  }, {
    question: 'What existing tech stack and systems does Minerva use?',
    context: 'List specifics: CMS (WordPress, Webflow, custom?), CRM (HubSpot, Salesforce?), marketing automation (Mailchimp, Marketo?), analytics (GA4, Mixpanel?), event platforms. We need to know what to integrate with.',
    type: 'open'
  }, {
    question: 'Where should quiz response data go? Who needs access?',
    context: 'Be specific: ☐ CRM as new leads ☐ Spreadsheet/CSV export ☐ Custom dashboard ☐ Marketing automation for follow-up sequences. Name the people who need to see the data and in what format.',
    type: 'open'
  }, {
    question: 'What are the compliance and legal requirements?',
    context: 'Specifics needed: ☐ GDPR (EU audiences?) ☐ Cookie consent required? ☐ Privacy policy URL to link to ☐ Medical/regulatory review needed for content? ☐ Legal sign-off required before launch? Who is the compliance contact?',
    type: 'open'
  }, {
    question: 'Do we need multi-language support? If yes, which languages?',
    context: 'List specific languages needed and whether translations will be provided by Minerva or need to be arranged.',
    type: 'open'
  }, {
    question: 'Are there specific browser, device, or accessibility requirements?',
    context: 'e.g. "Must work on iPad Safari in kiosk mode", "WCAG 2.1 AA compliance required", "Must support screen readers". List any known constraints.',
    type: 'open'
  }, {
    question: 'Is there an existing analytics or tracking setup we should use?',
    context: 'e.g. Google Tag Manager container ID, Meta Pixel, LinkedIn Insight Tag, UTM parameter conventions. Provide account details or tag IDs.',
    type: 'open'
  }, {
    question: 'What is the budget range and who approves spend?',
    context: 'Helps scope the build: static front-end only vs. full backend with real-time data, custom domain, ongoing maintenance, etc.',
    type: 'open'
  }]
}, {
  id: 'cta',
  icon: RocketIcon,
  title: 'Conversion & Follow-up',
  subtitle: 'What happens after the quiz?',
  color: '#059669',
  questions: [{
    question: 'What is the primary desired action after completing the quiz?',
    context: 'Book a demo, download a whitepaper, visit a specific page, sign up for a newsletter, talk to a rep at the booth, etc.',
    type: 'open'
  }, {
    question: 'Should there be a lead capture form (name, email, company)?',
    context: 'If yes, should it be before results (gate) or after results (optional)?',
    type: 'choice'
  }, {
    question: 'Are there specific URLs, assets, or landing pages the CTA should link to?',
    context: 'The mock-up embeds minervaimaging.com/publications and /about-us. Are these correct?',
    type: 'open'
  }, {
    question: 'Should there be a follow-up sequence?',
    context: 'e.g. automated email with results + relevant content 24h later',
    type: 'open'
  }, {
    question: 'How will success be measured?',
    context: 'Completion rate, lead capture rate, CTA click-through, time spent, shares, downstream conversions, etc.',
    type: 'open'
  }]
}];
function SectionCard({
  section,
  index
}: {
  section: Section;
  index: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const Icon = section.icon;
  return <div className="border border-slate-200 rounded-xl overflow-hidden bg-white print:break-inside-avoid print:border-slate-300" data-id="element-269">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-5 text-left print:pointer-events-none" data-id="element-270">
        <div className="flex items-center space-x-3" data-id="element-271">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 print:w-7 print:h-7" style={{
          backgroundColor: `${section.color}10`,
          border: `1px solid ${section.color}25`
        }} data-id="element-272">
            <Icon className="w-4 h-4" style={{
            color: section.color
          }} data-id="element-273" />
          </div>
          <div data-id="element-274">
            <h3 className="text-slate-900 font-semibold text-sm" data-id="element-275">
              {section.title}
            </h3>
            <p className="text-slate-500 text-xs mt-0.5" data-id="element-276">{section.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 print:hidden" data-id="element-277">
          <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded" data-id="element-278">
            {section.questions.length}
          </span>
          <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} data-id="element-279" />
        </div>
      </button>

      {expanded && <div className="px-5 pb-5 space-y-5 border-t border-slate-100" data-id="element-280">
          {section.questions.map((q, i) => <div key={i} className="relative pl-7 pt-4 print:break-inside-avoid" data-id="element-281">
              <span className="absolute left-0 top-4 text-xs font-bold tabular-nums w-5 text-right" style={{
          color: section.color
        }} data-id="element-282">
                {i + 1}.
              </span>
              <p className="text-sm text-slate-800 leading-relaxed font-medium" data-id="element-283">
                {q.question}
              </p>
              {q.context && <p className="text-xs text-slate-500 leading-relaxed mt-1.5 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 print:bg-transparent print:border-slate-200" data-id="element-284">
                  {q.context}
                </p>}
              <div className="mt-3" data-id="element-285">
                <div className="w-full min-h-[48px] rounded-lg border border-slate-200 bg-slate-50/50 flex items-start px-3 py-2 print:min-h-[60px] print:border-slate-300 print:bg-white" data-id="element-286">
                  <span className="text-[10px] text-slate-400 italic" data-id="element-287">
                    Notes:
                  </span>
                </div>
              </div>
            </div>)}
        </div>}
    </div>;
}
export function BriefingQuestionnaire() {
  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);
  return <div className="w-full min-h-screen bg-slate-50 text-slate-900 print:bg-white" data-id="element-288">
      <div className="max-w-2xl mx-auto px-6 py-10 print:px-0 print:py-4" data-id="element-289">
        {/* Header */}
        <div className="mb-10 print:mb-6" data-id="element-290">
          <div className="flex items-center justify-between mb-6" data-id="element-291">
            <div className="flex items-center space-x-3" data-id="element-292">
              <div className="bg-white border border-slate-200 px-3 py-1.5 rounded-lg shadow-sm print:shadow-none" data-id="element-293">
                <img src="https://cdn.magicpatterns.com/uploads/ifejLUcSApynCwwp5oN3Zr/Screenshot_2026-03-04_at_16.40.29.png" className="h-5 object-contain" alt="Minerva Imaging" data-id="element-294" />
              </div>
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest bg-slate-100 px-2.5 py-1 rounded print:bg-transparent" data-id="element-295">
                Briefing Document
              </span>
            </div>
            <button onClick={() => window.print()} className="flex items-center space-x-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors bg-white border border-slate-200 hover:border-slate-300 px-3 py-1.5 rounded-lg shadow-sm print:hidden" data-id="element-296">
              <PrinterIcon className="w-3.5 h-3.5" data-id="element-297" />
              <span data-id="element-298">Print / Save PDF</span>
            </button>
          </div>

          <h1 className="text-3xl font-semibold text-slate-900 leading-tight mb-3 print:text-2xl" data-id="element-299">
            Interactive Quiz — Briefing Questionnaire
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed mb-4" data-id="element-300">
            This document captures everything we need to know before building
            the production version of the oncology priorities quiz. The current
            version is a mock-up — these questions will help us replace
            assumptions with facts.
          </p>

          <div className="flex items-center space-x-4 text-xs text-slate-500" data-id="element-301">
            <div className="flex items-center space-x-1.5" data-id="element-302">
              <ClipboardListIcon className="w-3.5 h-3.5" data-id="element-303" />
              <span data-id="element-304">
                {totalQuestions} questions across {sections.length} sections
              </span>
            </div>
          </div>

          <div className="mt-6 h-px bg-slate-200" data-id="element-305" />
        </div>

        {/* Sections */}
        <div className="space-y-4 print:space-y-6" data-id="element-306">
          {sections.map((section, i) => <SectionCard key={section.id} section={section} index={i} data-id="element-307" />)}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-slate-200 text-center print:mt-8 print:pt-4" data-id="element-308">
          <p className="text-xs text-slate-500 leading-relaxed max-w-md mx-auto" data-id="element-309">
            Please complete this questionnaire before the briefing meeting.
            Answers don't need to be final — directional input helps us
            prioritize and scope the production build.
          </p>
        </div>
      </div>
    </div>;
}